import React, { useEffect, useState } from 'react';
import {
  Button,
  FormFeedback,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import convertDateToDateTimeLocalString from '@/utils/convertDateToDateTimeLocalString';
import { ContainerQrRedirectionCampaign } from '../../graphql/generatedTypes';
import { useRemoveContainerQrRedirectionCampaignMutation } from '../../graphql/mutations/removeContainerQrRedirectionCampaign.generated';
import { LoadingContainer } from '../../components/Layout';
import i18n from '@/utils/i18n';
import { ContainerType } from '../Events/OrderFulfillment/Components/OrderFulfillmentAddContainerTypes';
import ContainerTypeDisplay from '../../components/ContainerType/ContainerTypeDisplay';
import { useUpdateContainerQrRedirectCampaignByIdMutation } from '../../graphql/mutations/updateContainerQrRedirectCampaignById.generated';
import { FaCopy, FaTrash } from 'react-icons/fa';
import { LayoutStoreInstance } from '../../LayoutStore';

type ContainerQrRedirectionCampaignRowProps = {
  containerTypes: ContainerType[];
  containerQrRedirect: ContainerQrRedirectionCampaign;
  setRefresh: (status: boolean) => void;
};

const ContainerQrRedirectionCampaignRow = ({
  containerTypes,
  containerQrRedirect,
  setRefresh,
}: ContainerQrRedirectionCampaignRowProps) => {
  const {
    id,
    container_type_id,
    redirect_url,
    start_date,
    end_date,
    visit_count,
    comment,
    forward_container_short_id,
  } = containerQrRedirect;

  const currentDate = new Date();
  const startDate = new Date(start_date);
  const endDate = new Date(end_date);

  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string>(redirect_url);
  const [startDateInput, setStartDateInput] = useState<Date>(startDate);
  const [endDateInput, setEndDateInput] = useState<Date>(endDate);
  const [commentInput, setCommentInput] = useState<string>(comment || '');
  const [forwardContainerShortIdInput, setForwardContainerShortIdInput] =
    useState<boolean>(forward_container_short_id || false);

  const [removeContainerQrRedirectionCampaign, { loading: loadingRemoval }] =
    useRemoveContainerQrRedirectionCampaignMutation();

  const [updateContainerQrRedirectCampaignById, { loading: loadingUpdate }] =
    useUpdateContainerQrRedirectCampaignByIdMutation();

  const isInFuture = currentDate < startDateInput;
  const isActive = currentDate < endDateInput && currentDate > startDateInput;
  const isExpired = currentDate > endDateInput;
  const isStartAfterEnd = startDateInput > endDateInput;

  const containerType = containerTypes.find((cType) => {
    return cType.id === container_type_id;
  });

  const getCampaignStatus = () => {
    if (isInFuture) {
      return i18n.t('ContainerQrRedirectionCampaign:statusInFuture');
    }

    if (isActive) {
      return i18n.t('ContainerQrRedirectionCampaign:statusActive');
    }

    if (isExpired) {
      return i18n.t('ContainerQrRedirectionCampaign:statusExpired');
    }

    return i18n.t('ContainerQrRedirectionCampaign:statusUnknown');
  };

  const resetEditMode = () => {
    setRedirectUrl(redirect_url);
    setStartDateInput(startDate);
    setEndDateInput(endDate);
    setCommentInput(comment || '');
    setForwardContainerShortIdInput(forward_container_short_id || false);
  };

  useEffect(() => {
    if (!loadingRemoval) {
      setRefresh(true);
    }
  }, [loadingRemoval]);

  useEffect(() => {
    if (!loadingUpdate) {
      setRefresh(true);
    }
  }, [loadingUpdate]);

  return (
    <tr>
      <td className="align-middle">
        <ContainerTypeDisplay containerType={containerType} />
      </td>

      <td className="align-middle">
        {editMode && (
          <Input
            type="text"
            style={{ minWidth: '200px' }}
            value={redirectUrl}
            onChange={(event) => {
              setRedirectUrl(event.target.value);
            }}
            disabled={!editMode}
          />
        )}

        {!editMode && (
          <div className="d-flex">
            <Input
              type="text"
              className="me-1"
              style={{ minWidth: '200px' }}
              value={redirectUrl}
              title={redirectUrl}
              disabled
            />

            <Button
              onClick={() => {
                navigator.clipboard.writeText(redirectUrl);
                LayoutStoreInstance.setSuccess(i18n.t('Common:copied'));
              }}
            >
              <FaCopy />
            </Button>
          </div>
        )}
      </td>

      <td className="align-middle">
        <Input
          type="datetime-local"
          valid={isActive}
          invalid={isStartAfterEnd}
          value={convertDateToDateTimeLocalString(startDateInput)}
          onChange={(event) => {
            setStartDateInput(new Date(event.target.value));
          }}
          disabled={!editMode}
        />

        {editMode && isStartAfterEnd && (
          <FormFeedback>
            {i18n.t(
              'ContainerQrRedirectionCampaign:errorStartDateAfterEndDate',
            )}
          </FormFeedback>
        )}
      </td>

      <td className="align-middle">
        <Input
          type="datetime-local"
          valid={isActive}
          invalid={isExpired}
          value={convertDateToDateTimeLocalString(endDateInput)}
          onChange={(event) => {
            setEndDateInput(new Date(event.target.value));
          }}
          disabled={!editMode}
        />

        {isExpired && (
          <FormFeedback>
            {editMode &&
              i18n.t('ContainerQrRedirectionCampaign:errorDateInPast')}
            {!editMode &&
              i18n.t('ContainerQrRedirectionCampaign:errorCampaignExpired')}
          </FormFeedback>
        )}
      </td>
      <td className="numbers-formatted align-middle">{visit_count}</td>
      <td className="align-middle">
        {editMode ? (
          <Input
            type="textarea"
            style={{ minWidth: '200px' }}
            value={commentInput}
            onChange={(event) => {
              setCommentInput(event.target.value);
            }}
            placeholder={i18n.t(
              'ContainerQrRedirectionCampaign:commentPlaceholder',
            )}
          />
        ) : (
          <Input
            type="textarea"
            style={{ minWidth: '200px' }}
            value={comment || '-'}
            disabled
          />
        )}
      </td>
      <td className="align-middle">
        <Input
          type="checkbox"
          checked={forwardContainerShortIdInput}
          onChange={(event) => {
            setForwardContainerShortIdInput(event.target.checked);
          }}
          disabled={!editMode}
        />
      </td>
      <td className="align-middle">
        {(loadingRemoval || loadingUpdate) && <LoadingContainer />}

        {!loadingUpdate && !editMode && (
          <>
            <Button
              className="m-1"
              onClick={() => {
                setEditMode(true);
              }}
            >
              {i18n.t('Common:edit')}
            </Button>{' '}
          </>
        )}

        {!loadingUpdate && editMode && (
          <>
            <Button
              className="m-1"
              onClick={() => {
                updateContainerQrRedirectCampaignById({
                  variables: {
                    id: id,
                    container_qr_redirects: {
                      redirect_url: redirectUrl,
                      start_date: startDateInput.toISOString(),
                      end_date: endDateInput.toISOString(),
                      comment: commentInput,
                      forward_container_short_id: forwardContainerShortIdInput,
                    },
                  },
                });

                setEditMode(false);
              }}
            >
              {i18n.t('Common:save')}
            </Button>{' '}
          </>
        )}

        {!loadingUpdate && editMode && (
          <>
            <Button
              className="m-1"
              onClick={() => {
                resetEditMode();
                setEditMode(false);
              }}
            >
              {i18n.t('Common:cancel')}
            </Button>{' '}
          </>
        )}

        {!loadingRemoval && !editMode && (
          <Button
            className="m-1"
            onClick={() => {
              setShowRemoveModal(true);
            }}
          >
            <FaTrash /> {i18n.t('Common:delete')}
          </Button>
        )}
        <Modal
          isOpen={showRemoveModal}
          toggle={() => {
            setShowRemoveModal(false);
          }}
        >
          <ModalHeader
            toggle={() => {
              setShowRemoveModal(false);
            }}
          >
            {i18n.t('ContainerQrRedirectionCampaign:modalTitle')}
          </ModalHeader>
          <ModalBody>
            {i18n.t('ContainerQrRedirectionCampaign:modalBody')}: <p></p>
            {i18n.t('ContainerQrRedirectionCampaign:containerType')}:{' '}
            <p>
              <ContainerTypeDisplay containerType={containerType} />
            </p>
            {i18n.t('ContainerQrRedirectionCampaign:redirectUrl')}:{' '}
            <p>{redirect_url}</p>
            {i18n.t('ContainerQrRedirectionCampaign:status')}:{' '}
            <p>{getCampaignStatus()}</p>
            {i18n.t('ContainerQrRedirectionCampaign:visitCount')}:{' '}
            <p className="numbers-formatted">{visit_count}</p>
            {i18n.t('ContainerQrRedirectionCampaign:comment')}:{' '}
            <p>
              <Input type="textarea" value={comment || '-'} disabled />
            </p>
            {i18n.t('ContainerQrRedirectionCampaign:forwardContainerShortId')}:{' '}
            <p>
              {forward_container_short_id
                ? i18n.t('Common:yes')
                : i18n.t('Common:no')}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              onClick={() => {
                removeContainerQrRedirectionCampaign({
                  variables: { id: id },
                });

                setShowRemoveModal(false);
              }}
            >
              {i18n.t('Common:yes')}
            </Button>{' '}
            <Button
              color="secondary"
              onClick={() => {
                setShowRemoveModal(false);
              }}
            >
              {i18n.t('Common:no')}
            </Button>
          </ModalFooter>
        </Modal>
      </td>
    </tr>
  );
};

export default ContainerQrRedirectionCampaignRow;
